import { css } from '@emotion/react';
import { useTheme } from '@mui/material';

const useStyles = () => {
  const theme = useTheme();
  const { primary, background, error } = theme.palette;
  const { md, sm } = theme.breakpoints;
  const { shadows } = theme;

  const footerContainer = css`
    width: 100%;
    background: ${background.gray};
  `;

  const activateFooterContainer = css`
    width: 100%;
    background: ${background.darkBlue};
  `;

  const footerWrapper = css`
    width: 100%;
    max-width: 1396px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    padding: 75px 50px 70px;

    @media (max-width: ${`${md}px`}) {
      display: none;
    }
  `;

  const mobileFooterWrapper = css`
    width: 100%;
    max-width: 1256px;
    margin: 0 auto;
    display: none;
    flex-direction: column;

    @media (max-width: ${`${md}px`}) {
      padding: 50px 15px;
      display: flex;
    }
  `;

  const mobileActivateFooterWrapper = css`
    width: 100%;
    max-width: 1256px;
    margin: 0 auto;
    display: none;
    flex-direction: column;

    @media (max-width: ${`${md}px`}) {
      padding: 75px 55px;
      display: flex;
    }
  `;

  const footerColumns = css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 5px;

    & h5 {
      font-size: 16px;
      line-height: 20px;
      text-align: left;
      margin: 0 0 15px 0;
      font-weight: 700;
    }

    & a {
      font-size: 14px;
      line-height: 14px;
      text-align: left;
      margin: 0;
      font-weight: 200;
    }

    @media (max-width: ${`${md}px`}) {
      flex-direction: column;
    }
  `;

  const footerColumnLinks = css`
    display: flex;
    flex-direction: column;
    gap: 15px;
  `;

  const socialsWrapper = css`
    display: flex;
    justify-content: space-between;
    padding: 30px 0 0;
    margin: 56px 0 0;
    border-top: 1px solid ${primary.main};

    & > div {
      display: flex;
      align-items: center;
      gap: 15px;
      font-size: 14px;
      line-height: 21px;
      font-weight: 200;
    }

    & > div > p {
      margin: 0;
    }
    @media (max-width: ${`${md}px`}) {
      border-top: none;
      flex-direction: column-reverse;
      gap: 25px;
      padding: 25px 0 0;
      margin: 0;

      & > div {
        flex-wrap: wrap;

        & p {
          margin: 0;
        }
      }
    }
  `;

  const mobileSocialsWrapper = css`
    display: flex;
    justify-content: space-between;
    padding: 30px 0 0;
    margin: 56px 0 0;
    border-top: 1px solid ${primary.main};

    & > div {
      display: flex;
      align-items: center;
      gap: 15px;
      font-size: 14px;
      line-height: 21px;
      font-weight: 200;
    }

    & > div > p {
      margin: 0;
    }
    @media (max-width: ${`${md}px`}) {
      border-top: none;
      flex-direction: column-reverse;
      gap: 25px;
      padding: 25px 0 0;
      margin: 0;

      & > div {
        flex-wrap: wrap;

        & p {
          margin: 0;
        }
      }
    }
  `;

  const subscribeWrapper = css`
    margin-top: 105px;
    width: 268px;
    max-width: 268px;

    & h5 {
      font-size: 24px;
      line-height: 30px;
      margin: 0 0 25px 0;
      text-align: left;
      color: ${primary.main};
    }

    @media (max-width: ${`${md}px`}) {
      margin-top: 25px;
    }
  `;
  const subscribeInputWrapper = css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    @media (max-width: ${`${sm}px`}) {
      max-width: unset;
    }
  `;

  const subscribeInput = css`
    font-size: 14px;
    line-height: 14px;
    width: 100%;
    padding-right: 32px;

    & ::placeholder {
      color: ${background.gray2};
      font-weight: 300;
    }
  `;

  const subscribeSubmitButton = css`
    display: flex;
    align-items: center;
    width: 35px;
    min-width: 35px;
    max-width: 35px;
    height: 35px;
    max-height: 35px;
    border: 1px solid ${primary.main};
    border-radius: 50%;
    padding: 0;
    box-shadow: ${shadows[1]};

    &:not([disabled]):hover {
      border: 1px solid ${primary.main};
    }
  `;

  const collapseStyle = css`
    background: ${background.gray};
    border-bottom: 1px solid ${primary.main};

    @media (max-width: ${`${sm}px`}) {
      & > div {
        font-size: 16px;
        line-height: 20px;
      }
    }
  `;

  const footerMobileLinks = css`
    list-style-type: none;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    padding: 0;
    font-size: 14px;
    line-height: 21px;
    font-weight: 200;
  `;

  const spinner = css`
    width: 16px;
    height: 16px;
    border: 2px solid ${primary.main};
    border-top-color: transparent;
    animation: rotate 1s linear infinite;
    border-radius: 50%;

    @keyframes rotate {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `;

  const invalidStyle = css`
    & input:not([disabled]):not([readonly]) {
      color: ${error.main};
    }

    & .MuiInputBase-root::before,
    & .MuiInputBase-root::after {
      border-color: ${error.main};
    }

    & p {
      color: ${error.main};
    }

    & button:not([disabled]) {
      border: 1px solid ${error.main};
      color: ${error.main};
    }
  `;

  const statusStyle = css`
    margin: 0;
    margin-top: 6px;
    font-size: 14px;
    line-height: 18px;
  `;

  const minimalWrapper = css`
    display: flex;
    justify-content: space-between;
    padding: 50px 87px;
    margin: 0;
    border-top: none;

    & p {
      margin: 0;
    }

    @media (max-width: ${`${md}px`}) {
      padding: 50px 0px;
    }

    @media (max-width: ${`${sm}px`}) {
      padding: 0;
    }
  `;

  const colorWhite = css`
    color: white;
  `;

  const followUsText = css`
    color: white;
    font-size: 16px;
    font-weight: 400;
    line-height: 21px;

    @media (max-width: ${`${md}px`}) {
      margin-top: 15px;
    }

    span {
      text-decoration: underline;
    }
  `;

  const activateSocialWrapper = css`
    display: flex;
    gap: 15px;
    margin-top: 10px;
    justify-content: right;

    @media (max-width: ${`${md}px`}) {
      justify-content: left;
    }
  `;

  return {
    footerContainer,
    activateFooterContainer,
    footerWrapper,
    mobileFooterWrapper,
    footerColumns,
    footerColumnLinks,
    socialsWrapper,
    minimalWrapper,
    mobileSocialsWrapper,
    subscribeWrapper,
    subscribeInputWrapper,
    subscribeInput,
    subscribeSubmitButton,
    collapseStyle,
    footerMobileLinks,
    spinner,
    invalidStyle,
    statusStyle,
    colorWhite,
    followUsText,
    activateSocialWrapper,
    mobileActivateFooterWrapper,
  };
};

export default useStyles;
